import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  getFirestore,
} from "firebase/firestore";

import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import dayjs from "dayjs";

export default function CourtsCalendar() {
  const db = getFirestore();

  const [loading, setLoading] = useState(true);
  const [totalCourts, setTotalCourts] = useState(0);

  // NEW: Junior & Adult prices for 30 mins & 60 mins
  const [adultPrice30, setAdultPrice30] = useState(0);
  const [adultPrice60, setAdultPrice60] = useState(0);
  const [juniorPrice30, setJuniorPrice30] = useState(0);
  const [juniorPrice60, setJuniorPrice60] = useState(0);

  const [pennants, setPennants] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [timeSlots, setTimeSlots] = useState([]);

  const tableContainerRef = useRef(null);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDialogTab, setBookingDialogTab] = useState(0);

  const [courtsToBook, setCourtsToBook] = useState(1);
  const [bookingDuration, setBookingDuration] = useState(30);
  const [searchQuery, setSearchQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const [slotOverlaps, setSlotOverlaps] = useState([]);

  const dayMap = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

  // ------------------------------------------------------------
  // 1) Fetch data (including Junior & Adult prices)
  // ------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const settingsRef = doc(db, "pennantSettings", "settingsDoc");
        const settingsSnap = await getDoc(settingsRef);

        let fetchedCourts = 0;

        // Default sample prices
        let fetchedAdultPrice30 = 5;
        let fetchedAdultPrice60 = 9;
        let fetchedJuniorPrice30 = 3;
        let fetchedJuniorPrice60 = 6;

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          fetchedCourts = data.totalCourts || 0;

          // If fields are missing in Firestore, use defaults
          fetchedAdultPrice30 = data.adultPrice30 ?? 5;
          fetchedAdultPrice60 = data.adultPrice60 ?? 9;
          fetchedJuniorPrice30 = data.juniorPrice30 ?? 3;
          fetchedJuniorPrice60 = data.juniorPrice60 ?? 6;
        }

        // Pennants
        const pennantsSnap = await getDocs(collection(db, "Pennants"));
        const allPennants = pennantsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Bookings
        const bookingsSnap = await getDocs(collection(db, "Bookings"));
        const allBookings = bookingsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setTotalCourts(fetchedCourts);
        setAdultPrice30(fetchedAdultPrice30);
        setAdultPrice60(fetchedAdultPrice60);
        setJuniorPrice30(fetchedJuniorPrice30);
        setJuniorPrice60(fetchedJuniorPrice60);

        setPennants(allPennants);
        setBookings(allBookings);

        setLoading(false);
      } catch (err) {
        console.error("Error loading data:", err);
        setLoading(false);
      }
    })();
  }, [db]);

  // ------------------------------------------------------------
  // 2) Build timeslots
  // ------------------------------------------------------------
  const buildTimeSlots = useCallback(() => {
    const slots = [];
    let current = selectedDate.startOf("day");
    const endOfDay = current.hour(24).minute(0);

    while (current.isBefore(endOfDay)) {
      const start = current;
      const next = current.add(30, "minute");
      if (next.isAfter(endOfDay)) break;

      const used = computeUsedCourts(start, next);
      const free = Math.max(totalCourts - used, 0);

      slots.push({
        startDayjs: start,
        endDayjs: next,
        startTime: start.format("HH:mm"),
        endTime: next.format("HH:mm"),
        displayRange: `${start.format("h:mm A")} - ${next.format("h:mm A")}`,
        used,
        free,
      });

      current = next;
    }
    setTimeSlots(slots);
  }, [selectedDate, totalCourts, pennants, bookings]);

  const computeUsedCourts = useCallback((start, end) => {
    const slotStartMin = start.hour() * 60 + start.minute();
    const slotEndMin = end.hour() * 60 + end.minute();
    const dayOfWeekStr = dayMap[start.day()];
    let totalUsed = 0;

    // Overlapping Pennants
    pennants.forEach((p) => {
      if (!p.startDate || !p.endDate) return;
      const pStartDate = dayjs(p.startDate, "YYYY-MM-DD");
      const pEndDate = dayjs(p.endDate, "YYYY-MM-DD");
      if (selectedDate.isBefore(pStartDate, "day")) return;
      if (selectedDate.isAfter(pEndDate, "day")) return;

      if (dayOfWeekStr === p.pennantDay) {
        const [pSh, pSm] = (p.startTime || "00:00").split(":");
        const [pEh, pEm] = (p.endTime || "00:00").split(":");
        const pStartMin = parseInt(pSh, 10) * 60 + parseInt(pSm, 10);
        const pEndMin = parseInt(pEh, 10) * 60 + parseInt(pEm, 10);

        if (slotStartMin < pEndMin && pStartMin < slotEndMin) {
          totalUsed += parseInt(p.courtsRequired || "0", 10);
        }
      }
    });

    // Overlapping Bookings
    bookings.forEach((b) => {
      if (!b.date) return;
      const bDate = dayjs(b.date, "YYYY-MM-DD");
      if (!bDate.isSame(selectedDate, "day")) return;

      const [bSh, bSm] = (b.startTime || "00:00").split(":");
      const [bEh, bEm] = (b.endTime || "00:00").split(":");
      const bStartMin = parseInt(bSh, 10) * 60 + parseInt(bSm, 10);
      const bEndMin = parseInt(bEh, 10) * 60 + parseInt(bEm, 10);

      if (slotStartMin < bEndMin && bStartMin < slotEndMin) {
        totalUsed += parseInt(b.courtsUsed || "0", 10);
      }
    });

    return totalUsed;
  }, [selectedDate, pennants, bookings, dayMap]);

  useEffect(() => {
    if (!loading) {
      buildTimeSlots();
    }
  }, [loading, selectedDate, pennants, bookings, totalCourts, buildTimeSlots]);

  // Navigation & scrolling callbacks
  const handlePrevDay = useCallback(() => {
    setSelectedDate((prev) => prev.subtract(1, "day"));
  }, []);

  const handleNextDay = useCallback(() => {
    setSelectedDate((prev) => prev.add(1, "day"));
  }, []);

  const handleScrollUp = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({
        top: -200,
        behavior: "smooth",
      });
    }
  }, []);

  const handleScrollDown = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({
        top: 200,
        behavior: "smooth",
      });
    }
  }, []);

  // Overlaps
  const findSlotOverlaps = useCallback(
    (slot) => {
      const overlaps = [];
      if (!slot) return overlaps;

      const slotStartMin = slot.startDayjs.hour() * 60 + slot.startDayjs.minute();
      const slotEndMin = slot.endDayjs.hour() * 60 + slot.endDayjs.minute();
      const dayOfWeekStr = dayMap[slot.startDayjs.day()];

      // Bookings
      bookings.forEach((b) => {
        const bDate = dayjs(b.date, "YYYY-MM-DD");
        if (!bDate.isSame(selectedDate, "day")) return;

        const [bSh, bSm] = (b.startTime || "00:00").split(":");
        const [bEh, bEm] = (b.endTime || "00:00").split(":");
        const bStartMin = parseInt(bSh, 10) * 60 + parseInt(bSm, 10);
        const bEndMin = parseInt(bEh, 10) * 60 + parseInt(bEm, 10);

        if (slotStartMin < bEndMin && bStartMin < slotEndMin) {
          overlaps.push({ type: "booking", data: b });
        }
      });

      // Pennants
      pennants.forEach((p) => {
        const pStartDate = dayjs(p.startDate, "YYYY-MM-DD");
        const pEndDate = dayjs(p.endDate, "YYYY-MM-DD");
        if (selectedDate.isBefore(pStartDate, "day")) return;
        if (selectedDate.isAfter(pEndDate, "day")) return;
        if (dayOfWeekStr !== p.pennantDay) return;

        const [pSh, pSm] = (p.startTime || "00:00").split(":");
        const [pEh, pEm] = (p.endTime || "00:00").split(":");
        const pStartMin = parseInt(pSh, 10) * 60 + parseInt(pSm, 10);
        const pEndMin = parseInt(pEh, 10) * 60 + parseInt(pEm, 10);

        if (slotStartMin < pEndMin && pStartMin < slotEndMin) {
          overlaps.push({ type: "pennant", data: p });
        }
      });

      return overlaps;
    },
    [bookings, pennants, selectedDate, dayMap]
  );

  const handleSlotRowClick = useCallback(
    (slot) => {
      if (slot.free > 0) {
        setSelectedSlot(slot);
        setBookingDialogOpen(true);
        setBookingDialogTab(0);

        const overlapping = findSlotOverlaps(slot);
        setSlotOverlaps(overlapping);

        setCourtsToBook(1);
        setBookingDuration(30);
        setSearchQuery("");
        setMemberResults([]);
        setSelectedMembers([]);
      }
    },
    [findSlotOverlaps]
  );

  // Booking Dialog
  const handleCloseBookingDialog = useCallback(() => {
    setBookingDialogOpen(false);
    setSelectedSlot(null);
    setSlotOverlaps([]);
  }, []);

  const handleBookingDialogTabChange = useCallback((event, newValue) => {
    setBookingDialogTab(newValue);
  }, []);

  const handleBookingSave = useCallback(async () => {
    if (!selectedSlot) return;
    const { free, startDayjs } = selectedSlot;
    const toBook = parseInt(courtsToBook, 10);

    if (selectedMembers.length === 0) {
      alert("Please add at least one member.");
      return;
    }
    if (toBook < 1 || toBook > free) {
      alert(`Invalid number of courts. You can book 1 to ${free} courts.`);
      return;
    }

    const endDayjs = startDayjs.add(bookingDuration, "minute");
    const endOfDay = selectedDate.startOf("day").hour(24).minute(0);
    if (endDayjs.isAfter(endOfDay)) {
      alert("Booking cannot cross into the next day. Please reduce duration.");
      return;
    }

    const current = startDayjs.clone();
    let allFree = true;
    while (current.isBefore(endDayjs)) {
      const blockStart = current;
      const blockEnd = current.add(30, "minute");
      const used = computeUsedCourts(blockStart, blockEnd);
      const blockFree = Math.max(totalCourts - used, 0);
      if (blockFree < toBook) {
        allFree = false;
        break;
      }
    }
    if (!allFree) {
      alert("Not enough courts are free for the entire duration.");
      return;
    }

    const bookingStartTime = startDayjs.format("HH:mm");
    const bookingEndTime = endDayjs.format("HH:mm");

    try {
      await addDoc(collection(db, "Bookings"), {
        date: selectedDate.format("YYYY-MM-DD"),
        startTime: bookingStartTime,
        endTime: bookingEndTime,
        courtsUsed: toBook,
        members: selectedMembers.map((m) => m.id),
      });

      const snap = await getDocs(collection(db, "Bookings"));
      const allBookings = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBookings(allBookings);

      buildTimeSlots();
      handleCloseBookingDialog();
    } catch (err) {
      console.error("Error creating booking:", err);
      alert("Failed to create booking.");
    }
  }, [
    selectedSlot,
    selectedMembers,
    bookingDuration,
    buildTimeSlots,
    computeUsedCourts,
    handleCloseBookingDialog,
    selectedDate,
    totalCourts,
    db,
    courtsToBook,
  ]);

  // Searching for members
  const handleSearchMembers = useCallback(async () => {
    try {
      const q = searchQuery.toLowerCase();
      const snap = await getDocs(collection(db, "members"));
      const allMembers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const results = allMembers.filter((pm) => {
        const fullName = `${pm.firstName || ""} ${pm.lastName || ""}`.toLowerCase();
        const email = (pm.email || "").toLowerCase();
        return fullName.includes(q) || email.includes(q);
      });
      setMemberResults(results);
    } catch (err) {
      console.error("Error searching members:", err.message);
    }
  }, [searchQuery, db]);

  const handleAddMember = useCallback(
    (member) => {
      if (selectedMembers.some((m) => m.id === member.id)) return;
      setSelectedMembers((prev) => [...prev, member]);
    },
    [selectedMembers]
  );

  const handleRemoveMember = useCallback((id) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // Table color
  const getSlotColor = useCallback(
    (free) => {
      if (free === 0) return "lightgray";
      if (free === totalCourts) return "lightgreen";
      if (free <= 2) return "#FFB6C1";
      if (free < totalCourts / 2) return "orange";
      return "lightgreen";
    },
    [totalCourts]
  );
// eslint-disable-next-line arrow-body-style
  const renderCourtTable = useCallback(() => {
    return (
      <Box sx={{ maxHeight: "600px", overflowY: "auto", mb: 1 }} ref={tableContainerRef}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Time Range</TableCell>
              <TableCell align="center">Free Courts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSlots.map((slot, i) => {
              const bgColor = getSlotColor(slot.free);
              return (
                <TableRow
                  key={i}
                  sx={{ cursor: slot.free > 0 ? "pointer" : "default" }}
                  style={{ backgroundColor: bgColor }}
                  onClick={() => handleSlotRowClick(slot)}
                >
                  <TableCell>{slot.displayRange}</TableCell>
                  <TableCell align="center">{slot.free}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }, [timeSlots, getSlotColor, handleSlotRowClick]);

  // handleSavePricing now saves 4 fields (Adult/Junior 30 & 60)
  const handleSavePricing = useCallback(async () => {
    try {
      const settingsRef = doc(db, "pennantSettings", "settingsDoc");
      await updateDoc(settingsRef, {
        adultPrice30: Number(adultPrice30),
        adultPrice60: Number(adultPrice60),
        juniorPrice30: Number(juniorPrice30),
        juniorPrice60: Number(juniorPrice60),
      });
      alert("Pricing updated successfully!");
    } catch (err) {
      console.error("Error saving pricing:", err);
      alert("Failed to save pricing.");
    }
  }, [db, adultPrice30, adultPrice60, juniorPrice30, juniorPrice60]);

  if (loading) {
    return (
      <Container>
        <Typography variant="h5" sx={{ mt: 4 }}>
          Loading Calendar...
        </Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h4" gutterBottom>
            Courts Calendar
          </Typography>

          {/* Some color key example */}
          <Box sx={{ mb: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 20, height: 20, bgcolor: "lightgreen", borderRadius: 1 }} />
                <Typography variant="body2">All or most courts free</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 20, height: 20, bgcolor: "orange", borderRadius: 1 }} />
                <Typography variant="body2">Less than half courts free</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 20, height: 20, bgcolor: "#FFB6C1", borderRadius: 1 }} />
                <Typography variant="body2">Only 1-2 courts free</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 20, height: 20, bgcolor: "lightgray", borderRadius: 1 }} />
                <Typography variant="body2">No courts free</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Date selection */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="contained" onClick={handlePrevDay}>
              Prev Day
            </Button>

            <DatePicker
              label="Select Date"
              format="DD/MM/YYYY"
              value={selectedDate}
              onChange={(newVal) => {
                if (newVal) setSelectedDate(newVal);
              }}
            />

            <Button variant="contained" onClick={handleNextDay}>
              Next Day
            </Button>
          </Stack>

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Total Courts: {totalCourts}
          </Typography>

          {/* Scroll up */}
          <Stack direction="row" justifyContent="center" sx={{ mb: 1 }}>
            <IconButton color="primary" onClick={handleScrollUp}>
              <ArrowUpwardIcon />
            </IconButton>
          </Stack>

          {renderCourtTable()}

          {/* Scroll down */}
          <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
            <IconButton color="primary" onClick={handleScrollDown}>
              <ArrowDownwardIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Booking Pricing Settings (Adult & Junior) */}
        <Box sx={{ mt: 4, mb: 4, p: 2, border: "1px solid #ccc" }}>
          <Typography variant="h6" gutterBottom>
            Court Booking Prices
          </Typography>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Adult 30 mins ($)"
                type="number"
                value={adultPrice30}
                onChange={(e) => setAdultPrice30(e.target.value)}
              />
              <TextField
                label="Adult 1 hour ($)"
                type="number"
                value={adultPrice60}
                onChange={(e) => setAdultPrice60(e.target.value)}
              />
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Junior 30 mins ($)"
                type="number"
                value={juniorPrice30}
                onChange={(e) => setJuniorPrice30(e.target.value)}
              />
              <TextField
                label="Junior 1 hour ($)"
                type="number"
                value={juniorPrice60}
                onChange={(e) => setJuniorPrice60(e.target.value)}
              />
            </Stack>
          </Stack>

          <Button variant="contained" onClick={handleSavePricing}>
            Save Pricing
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            These prices will be used on the member side when booking courts.
          </Typography>
        </Box>

        {/* Booking Dialog */}
        <Dialog open={bookingDialogOpen} onClose={handleCloseBookingDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Court Slot: {selectedSlot ? selectedSlot.displayRange : ""}
          </DialogTitle>

          <Tabs
            value={bookingDialogTab}
            onChange={handleBookingDialogTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Book Court" />
            <Tab label="Existing Bookings" />
          </Tabs>

          <DialogContent dividers>
            {/* Tab 0 => Booking form */}
            {bookingDialogTab === 0 && selectedSlot && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Start: {selectedSlot.startDayjs.format("h:mm A")} on{" "}
                  {selectedDate.format("DD/MM/YYYY")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Free Courts: {selectedSlot.free}
                </Typography>

                <TextField
                  label="Number of Courts to Book"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={courtsToBook}
                  onChange={(e) => setCourtsToBook(e.target.value)}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="duration-label">Booking Duration</InputLabel>
                  <Select
                    labelId="duration-label"
                    value={bookingDuration}
                    label="Booking Duration"
                    onChange={(e) => setBookingDuration(parseInt(e.target.value, 10))}
                  >
                    <MenuItem value={30}>30 mins</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1 hr 30 mins</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>

                {/* Search for Members */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">Add Members</Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      label="Search by name or email"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Button variant="contained" onClick={handleSearchMembers}>
                      Search
                    </Button>
                  </Box>

                  {/* Search results */}
                  {memberResults.length > 0 && (
                    <Card sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2">Search Results:</Typography>
                        {memberResults.map((m) => (
                          <Box
                            key={m.id}
                            sx={{ p: 1, mb: 1, backgroundColor: "#f5f5f5", cursor: "pointer" }}
                            onClick={() => handleAddMember(m)}
                          >
                            <Typography variant="body2">
                              {m.firstName} {m.lastName} ({m.email})
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Selected Members List */}
                  {selectedMembers.length > 0 && (
                    <Card sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2">Selected Members:</Typography>
                        {selectedMembers.map((m) => (
                          <Box
                            key={m.id}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 1,
                              mb: 1,
                              backgroundColor: "#e0f7fa",
                            }}
                          >
                            <Typography variant="body2">
                              {m.firstName} {m.lastName} ({m.email})
                            </Typography>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleRemoveMember(m.id)}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 1 => Existing Overlaps */}
            {bookingDialogTab === 1 && (
              <Box>
                {slotOverlaps.length === 0 ? (
                  <Typography>No bookings or pennants in this slot.</Typography>
                ) : (
                  <List>
                    {slotOverlaps.map((ev, idx) => {
                      if (ev.type === "booking") {
                        const b = ev.data;
                        return (
                          <Box key={`bk-${b.id}-${idx}`}>
                            <ListItem>
                              <ListItemText
                                primary={`Booking: ${b.startTime} - ${b.endTime}`}
                                secondary={`Courts Used: ${b.courtsUsed}, Members: ${b.members?.length || 0}`}
                              />
                            </ListItem>
                            <Divider />
                          </Box>
                        );
                      }
                      if (ev.type === "pennant") {
                        const p = ev.data;
                        return (
                          <Box key={`pn-${p.id}-${idx}`}>
                            <ListItem>
                              <ListItemText
                                primary={`Pennant: ${p.name}`}
                                secondary={`Day: ${p.pennantDay} | Start: ${p.startTime} - ${
                                  p.endTime
                                } | Courts: ${p.courtsRequired || 0}`}
                              />
                            </ListItem>
                            <Divider />
                          </Box>
                        );
                      }
                      return null;
                    })}
                  </List>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseBookingDialog}>Close</Button>
            {bookingDialogTab === 0 && (
              <Button variant="contained" onClick={handleBookingSave}>
                Save Booking
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}
